import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import AudioPlayer from './AudioPlayer';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import AppLoadingState from './common/AppLoadingState';
import { AppPageShell, PageHeader } from './common/ui';

const formatDate = (dateString) => {
  try {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return format(date, 'PPpp');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

const formatTime = (dateString) => {
  try {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Time';
    return format(date, 'HH:mm:ss');
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid Time';
  }
};

const CallDetails = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [callDetails, setCallDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'overview';
  });
  const [messageVersions, setMessageVersions] = useState(new Map());
  const eventSourceRef = useRef(null);
  const [shouldStream, setShouldStream] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [analyzingEmotions, setAnalyzingEmotions] = useState(false);
  const [emotionResults, setEmotionResults] = useState(null);

  const fetchCallDetails = useCallback(async () => {
    try {
      const data = await api.getCallDetails(callId);
      setCallDetails(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(data)) {
          return data;
        }
        return prev;
      });
    } catch (error) {
      console.error('Error fetching call details:', error);
    } finally {
      setLoading(false);
    }
  }, [callId]);

  const fetchRecording = useCallback(async () => {
    try {
      setLoadingRecording(true);
      const blob = await api.downloadCallRecording(callId);
      const url = URL.createObjectURL(blob);
      setRecordingUrl(url);
    } catch (error) {
      console.error('Error fetching recording:', error);
      toast.error('Failed to fetch recording');
    } finally {
      setLoadingRecording(false);
    }
  }, [callId]);

  useEffect(() => {
    fetchCallDetails();
  }, [fetchCallDetails]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', activeTab);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, [activeTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  useEffect(() => {
    const isCallActive = callDetails && !callDetails.completed;
    const shouldBeStreaming = activeTab === 'events' && isCallActive;
    setShouldStream(shouldBeStreaming);
  }, [activeTab, callDetails]);

  useEffect(() => {
    if (shouldStream) {
      console.log('Starting event stream...');
      eventSourceRef.current = api.createEventStream(callId);
      
      eventSourceRef.current.onmessage = (event) => {
        try {
          const eventData = JSON.parse(event.data);
          console.log('Received event data:', eventData);
          
          if (eventData.error) {
            console.error('Event stream error:', eventData.error);
            return;
          }

          setMessageVersions(prev => {
            const newVersions = new Map(prev);
            const messageKey = `${eventData.user}-${eventData.id}`;
            
            if (eventData.final) {
              const completionKey = `system-completion-${Date.now()}`;
              newVersions.set(completionKey, {
                level: 'info',
                message: eventData.message || 'Call completed',
                category: 'system',
                timestamp: eventData.timestamp,
                eventId: eventData.eventId,
                id: completionKey
              });
              
              fetchCallDetails();
              setShouldStream(false);
              if (eventSourceRef.current) {
                eventSourceRef.current.close();
              }
              
              return newVersions;
            }
            
            const currentVersion = newVersions.get(messageKey);
            if (!currentVersion || 
                eventData.message.length > currentVersion.message.length ||
                eventData.eventId > currentVersion.eventId) {
              newVersions.set(messageKey, eventData);
            }
            
            return newVersions;
          });
        } catch (error) {
          console.error('Error processing event:', error);
        }
      };

      eventSourceRef.current.onerror = (error) => {
        if (eventSourceRef.current?.readyState === EventSource.CLOSED) {
          console.log('Connection closed normally');
        } else if (eventSourceRef.current?.readyState === EventSource.CONNECTING) {
          console.log('Connection attempting to reconnect...');
        } else {
          console.error('Connection error:', error);
          setShouldStream(false);
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
          }
        }
      };

      return () => {
        console.log('Cleaning up event stream...', eventSourceRef.current?.readyState);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
      };
    }
  }, [callId, shouldStream, fetchCallDetails]);

  useEffect(() => {
    if (callDetails?.completed && shouldStream) {
      console.log('Call completed, closing stream...');
      setShouldStream(false);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    }
  }, [callDetails?.completed, shouldStream]);

  useEffect(() => {
    if (activeTab === 'recording' && callDetails?.completed && !recordingUrl) {
      fetchRecording();
    }
  }, [activeTab, callDetails?.completed, recordingUrl, fetchRecording]);

  // Add cleanup for recording URL
  useEffect(() => {
    return () => {
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
      }
    };
  }, [recordingUrl]);

  const handleAnalyzeEmotions = async () => {
    try {
      setAnalyzingEmotions(true);
      const response = await api.analyzeCallEmotions(callId);
      setEmotionResults(response.data.data);
      toast.success('Emotion analysis completed');
    } catch (error) {
      toast.error(error.message || 'Failed to analyze emotions');
    } finally {
      setAnalyzingEmotions(false);
    }
  };

  const renderEventBadge = (category) => {
    const colors = {
      info: 'bg-blue-100 text-blue-800',
      performance: 'bg-green-100 text-green-800',
      error: 'bg-red-100 text-red-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const events = Array.from(messageVersions.values());

  if (loading) {
    return (
      <AppLoadingState layout="section" showLabel={false} className="min-h-[50vh]" />
    );
  }

  if (!callDetails) {
    return <div className="text-center">Call not found</div>;
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'transcripts', label: 'Transcripts' },
    { id: 'events', label: 'Live Events' },
    { id: 'recording', label: 'Recording' },
    { id: 'variables', label: 'Variables' },
    { id: 'metadata', label: 'Metadata' }
  ];

  const handleBack = () => {
    navigate('/app/calls');
  };

  return (
    <AppPageShell maxWidthClass="max-w-7xl">
      <PageHeader
        compact
        eyebrow="Calls"
        title="Call details"
        description={
          callDetails?.to
            ? `Destination ${callDetails.to} · ID ${callDetails.call_id || callId}`
            : `Call ID ${callDetails?.call_id || callId}`
        }
        lead={
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-semibold text-[var(--app-muted)] transition hover:bg-[var(--app-accent-soft)] hover:text-[var(--app-ink)]"
          >
            <span aria-hidden>←</span>
            Call history
          </button>
        }
      />

      <div className="sticky top-0 z-10 border-b border-[var(--app-border)] bg-[var(--app-surface-strong)]/95 backdrop-blur-sm">
        <div className="overflow-x-auto px-2 sm:px-4">
          <nav className="flex min-w-max gap-4 py-2" aria-label="Call detail sections">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-[var(--app-accent)] text-[var(--app-accent)]'
                    : 'border-transparent text-[var(--app-muted)] hover:border-[var(--app-border)] hover:text-[var(--app-ink)]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-4 sm:p-6">
        <div className="bg-white shadow rounded-lg">
          {activeTab === 'overview' && (
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Call Information</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Call ID</dt>
                      <dd className="mt-1 text-sm text-gray-900">{callDetails.call_id}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Created At</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {formatDate(callDetails.created_at)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Duration</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof callDetails.call_length === 'number' 
                          ? `${callDetails.call_length.toFixed(2)} minutes`
                          : 'N/A'
                        }
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="mt-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          callDetails.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {callDetails.completed ? 'Completed' : 'In Progress'}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-4">Call Summary</h3>
                  <p className="text-sm text-gray-600">{callDetails.summary || 'No summary available'}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transcripts' && (
            <div className="divide-y divide-gray-200">
              {callDetails.transcripts?.map((transcript) => (
                <div
                  key={transcript.id}
                  className={`p-4 ${
                    transcript.user === 'assistant' ? 'bg-blue-50' : 'bg-white'
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {transcript.user === 'assistant' ? 'AI Assistant' : 'User'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTime(transcript.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{transcript.text}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'events' && (
            <div className="divide-y divide-gray-200">
              {events.length === 0 ? (
                <div className="p-4 text-center">
                  {callDetails?.completed ? (
                    <div className="space-y-2">
                      <p className="text-gray-500">This call has been completed.</p>
                      <p className="text-sm text-gray-400">
                        View the conversation in the{' '}
                        <button 
                          onClick={() => setActiveTab('transcripts')}
                          className="text-blue-500 hover:text-blue-600"
                        >
                          Transcripts
                        </button>
                        {' '}tab.
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-500">No events yet</p>
                  )}
                </div>
              ) : (
                [...events]
                  .sort((a, b) => b.eventId - a.eventId)
                  .map((event) => (
                    <div
                      key={`${event.user}-${event.id}-${event.eventId}`}
                      className={`p-4 ${
                        event.category === 'ai' ? 'bg-blue-50' : 'bg-white'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${renderEventBadge(event.category)}`}>
                          {event.category === 'ai' ? 'AI Assistant' : 
                           event.category === 'user' ? 'User' : 
                           event.category}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatTime(event.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{event.message}</p>
                    </div>
                  ))
              )}
            </div>
          )}

          {activeTab === 'recording' && (
            <div className="p-4">
              {loadingRecording ? (
                <AppLoadingState layout="compact" label="Loading recording…" className="!min-h-[10rem]" />
              ) : recordingUrl ? (
                <div className="space-y-6">
                  <div className="max-w-2xl mx-auto">
                    <AudioPlayer 
                      src={recordingUrl}
                      onError={(e) => console.error('Audio playback error:', e)}
                      callId={callId}
                    />
                  </div>
                  
                  <div className="max-w-2xl mx-auto">
                    <div className="flex justify-between items-center">
                      <button
                        onClick={handleAnalyzeEmotions}
                        disabled={analyzingEmotions}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                          analyzingEmotions 
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                      >
                        {analyzingEmotions ? 'Analyzing...' : 'Analyze Emotions'}
                      </button>
                    </div>

                    {emotionResults && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-lg font-medium mb-2">Emotion Analysis Results</h3>
                        <div className="space-y-2">
                          <p className="text-sm">
                            <span className="font-medium">Detected Emotion:</span>{' '}
                            <span className="capitalize">{emotionResults.emotion}</span>
                          </p>
                          <p className="text-sm">
                            <span className="font-medium">Analyzed At:</span>{' '}
                            {formatDate(emotionResults.analyzedAt)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    {callDetails?.completed 
                      ? 'No recording available for this call.' 
                      : 'Recording will be available once the call is completed.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {(activeTab === 'variables' || activeTab === 'metadata') && (
            <div className="p-4">
              <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
                {JSON.stringify(
                  activeTab === 'variables' ? callDetails.variables : callDetails.metadata, 
                  null, 
                  2
                )}
              </pre>
            </div>
          )}
        </div>
      </div>
    </AppPageShell>
  );
};

export default CallDetails; 